import React, { forwardRef, ReactNode, useEffect } from 'react'
import { Platform } from 'react-native'
import { twMerge } from 'tailwind-merge'

import {
    Modal as GsModal,
    ModalBackdrop,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
} from '../../_gluestack/modal'
import { FOCUS_RING_CLASS } from '../../focusRing'
import { Box } from '../box'
import { CloseGlyph } from '../primitive-glyphs/glyphs'
import { Text } from '../text'

export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full'

export interface ModalProps {
    isOpen: boolean
    onClose: () => void
    title?: ReactNode
    footer?: ReactNode
    footerSeparator?: boolean
    size?: ModalSize
    isDismissible?: boolean
    /** Whether a backdrop click closes the modal. Defaults to `isDismissible`.
     *  Set `false` (with `isDismissible` left true) to keep the X close button
     *  but prevent accidental dismissal — e.g. data-entry forms. */
    closeOnOverlayClick?: boolean
    scrollable?: boolean
    children?: ReactNode
    className?: string
    bodyClassName?: string
    nativeID?: string
}

const contentBase =
    'rounded-lg border border-solid border-border-subtle bg-background-sub-surface overflow-hidden'

/**
 * Makes `isDismissible={false}` actually block Escape on web.
 *
 * Gluestack's `isKeyboardDismissable` is a no-op on web: `useBackHandler`'s web
 * branch attaches its `keyup` Escape listener to `document.body` unconditionally
 * and only the native branch honours `enabled`. So a non-dismissible modal —
 * including one held open mid-mutation — was still closable with one keypress.
 *
 * Their listener is a bubble-phase one on `body`, so a capture-phase listener on
 * `document` runs first and `stopPropagation()` keeps the event from ever
 * reaching it. Keyed on `keyup` to match the listener being suppressed.
 */
const useBlockEscapeWhenLocked = (isOpen: boolean, isDismissible: boolean) => {
    useEffect(() => {
        if (Platform.OS !== 'web' || !isOpen || isDismissible) {
            return undefined
        }
        const swallowEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation()
            }
        }
        document.addEventListener('keyup', swallowEscape, true)
        return () => document.removeEventListener('keyup', swallowEscape, true)
    }, [isOpen, isDismissible])
}

export const Modal = forwardRef<React.ComponentRef<typeof ModalContent>, ModalProps>(function Modal(
    {
        isOpen,
        onClose,
        title,
        footer,
        footerSeparator = true,
        size = 'md',
        isDismissible = true,
        closeOnOverlayClick,
        scrollable = true,
        children,
        className,
        bodyClassName,
        nativeID,
    },
    ref,
) {
    useBlockEscapeWhenLocked(isOpen, isDismissible)

    // Gluestack's Escape listener stays attached while the modal is mounted but
    // closed, so a stray Escape would still fire onClose — which re-opens modals
    // whose close handler is a toggle (e.g. the advanced filters).
    const handleClose = () => {
        if (isOpen) {
            onClose()
        }
    }

    return (
        <GsModal
            isOpen={isOpen}
            onClose={handleClose}
            size={size}
            closeOnOverlayClick={closeOnOverlayClick ?? isDismissible}
            // Escape has to follow `isDismissible` too, otherwise a modal that hides its
            // close button (e.g. mid-mutation) is still dismissible by keyboard. This
            // prop only takes effect on native — `useBlockEscapeWhenLocked` above is what
            // enforces it on web.
            isKeyboardDismissable={isDismissible}
            nativeID={nativeID}
        >
            <ModalBackdrop
                className='bg-black/40 [backdrop-filter:blur(12px)]'
                animate={{ opacity: 1 }}
            />
            <ModalContent
                ref={ref}
                className={twMerge(contentBase, scrollable && 'max-h-[90vh]', className)}
            >
                {title !== undefined && (
                    <ModalHeader className='flex-row items-center justify-between gap-3 border-b border-solid border-border-subtle px-6 py-4'>
                        {typeof title === 'string' ? (
                            <Text size='heading' tone='default'>
                                {title}
                            </Text>
                        ) : (
                            title
                        )}
                        {isDismissible && (
                            <ModalCloseButton
                                onPress={onClose}
                                className={twMerge('web:hover:opacity-70', FOCUS_RING_CLASS)}
                            >
                                <CloseGlyph size={24} />
                            </ModalCloseButton>
                        )}
                    </ModalHeader>
                )}
                {scrollable ? (
                    <ModalBody
                        className={twMerge('bg-background-surface px-6 py-6', bodyClassName)}
                    >
                        {children}
                    </ModalBody>
                ) : (
                    // Figma splits every modal into a sub-surface header/footer
                    // around a surface body — that split does not depend on
                    // whether the body scrolls, so the non-scrolling branch gets
                    // the same token as ModalBody above it.
                    <Box className={twMerge('flex-1 bg-background-surface', bodyClassName)}>
                        {children}
                    </Box>
                )}
                {/* Figma `Modal/Footer` is 72px around a 48px action button, so 12px —
                    the header's 56px around a 24px title is what makes it `py-4`. */}
                {footer !== undefined && (
                    <ModalFooter
                        className={twMerge(
                            'flex-row items-center justify-end gap-2 px-6 py-3',
                            footerSeparator && 'border-t border-solid border-border-subtle',
                        )}
                    >
                        {footer}
                    </ModalFooter>
                )}
            </ModalContent>
        </GsModal>
    )
})
Modal.displayName = 'Modal'
